import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { observer } from 'mobx-react'
import { Rect } from 'react-konva'

import { getToolCursor } from '~/client/src/shared/components/SitemapHelpers/utils/locationCursor'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  MAX_RELATIVE_POSITION,
  sitemapMousePointTo,
  validatePosition,
} from '~/client/src/shared/utils/SitemapCalculationHelpers'

import MapViewItemsSetupStore from '../../stores/MapViewItemsSetup.store'

const DEFAULT_CURSOR = 'default'

interface IProps {
  containerWidth: number
  containerHeight: number
  store?: MapViewItemsSetupStore
}

@observer
export default class CreateNewItemArea extends React.Component<IProps> {
  private armedCursor: string = null
  private isHovered: boolean = false

  public componentDidMount() {
    this.armedCursor = this.toolCursor
  }

  // Neither disarming nor an armed -> armed switch fires a Konva mouse event.
  public componentDidUpdate() {
    const cursor = this.toolCursor
    if (cursor !== this.armedCursor) {
      if (this.isHovered) {
        document.body.style.cursor = cursor || DEFAULT_CURSOR
      }
      if (!cursor) {
        // The hit area is gone, so no mouse-leave will ever arrive for it.
        this.isHovered = false
      }
    }
    this.armedCursor = cursor
  }

  public componentWillUnmount() {
    if (this.isHovered) {
      this.setDefaultCursor()
    }
  }

  public render() {
    const { containerHeight, containerWidth } = this.props

    if (!this.isToolArmed) {
      return null
    }

    return (
      <Rect
        x={0}
        y={0}
        width={containerWidth}
        height={containerHeight}
        onMouseEnter={this.handleAreaMouseEnter}
        onMouseLeave={this.handleAreaMouseLeave}
        onClick={this.onAreaClick}
        id="create-new-area"
      />
    )
  }

  private get isToolArmed(): boolean {
    return Boolean(this.toolCursor)
  }

  // One glyph table for every tool: the text sticker resolves through its own icon rather than
  // a second cursor constant. getToolCursor answers null only when nothing is armed, which is
  // what gates the hit area below.
  private get toolCursor(): string {
    const { store } = this.props
    if (!store) {
      return null
    }
    const iconName = store.isTextStickerCreationActive
      ? MapViewLocationIcon.TextBox
      : store.creatableAttributeIcon
    return getToolCursor(store.creatableAttributeType, iconName)
  }

  private handleAreaMouseEnter = () => {
    this.isHovered = true
    document.body.style.cursor = this.toolCursor || DEFAULT_CURSOR
  }

  private handleAreaMouseLeave = () => {
    this.isHovered = false
    this.setDefaultCursor()
  }

  private setDefaultCursor() {
    document.body.style.cursor = DEFAULT_CURSOR
  }

  private onAreaClick = (event: KonvaEventObject<MouseEvent>) => {
    const { containerWidth, containerHeight, store } = this.props

    const stage = event.currentTarget.getStage()

    if (!containerWidth || !containerHeight) {
      return
    }

    const mousePointTo = sitemapMousePointTo(
      stage,
      containerWidth,
      containerHeight,
    )
    const relativePosition = validatePosition({
      x: (mousePointTo.x / containerWidth) * MAX_RELATIVE_POSITION,
      y: (mousePointTo.y / containerHeight) * MAX_RELATIVE_POSITION,
    })

    store.createAttributeInPosition(relativePosition)
    this.setDefaultCursor()
  }
}
